import React, { useState, useEffect, useRef } from 'react';
import { Modal } from 'react-bootstrap';

interface EditNoteModalProps {
    show: boolean;
    onHide: () => void;
    note: {
        id: number;
        titulo: string;
        observacoes: string;
        arquivos: Array<{
            nome_original: string;
            nome_salvo: string;
            tipo: string;
            tamanho: number;
        }>;
    } | null;
    onSave: (id: number, data: FormData) => Promise<void>;
}

interface UploadedFile extends File {
    id?: string;
    progress?: number;
}

const EditNoteModal: React.FC<EditNoteModalProps> = ({ show, onHide, note, onSave }) => {
    const [titulo, setTitulo] = useState('');
    const [observacoes, setObservacoes] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [arquivosParaRemover, setArquivosParaRemover] = useState<string[]>([]);
    const [novosArquivos, setNovosArquivos] = useState<UploadedFile[]>([]);
    const fileInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (note) {
            setTitulo(note.titulo);
            setObservacoes(note.observacoes);
            setArquivosParaRemover([]);
            setNovosArquivos([]);
        }
    }, [note]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!note) return;

        try {
            setIsSubmitting(true);
            const formData = new FormData();
            formData.append('titulo', titulo);
            formData.append('observacoes', observacoes);
            
            // Adiciona os arquivos novos
            novosArquivos.forEach(file => {
                formData.append('arquivos[]', file);
            });

            // Adiciona a lista de arquivos para remover
            if (arquivosParaRemover.length > 0) {
                formData.append('remover_arquivos', JSON.stringify(arquivosParaRemover));
            }

            await onSave(note.id, formData);
            onHide();
        } catch (error) {
            console.error('Erro ao atualizar anotação:', error);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleRemoveFile = (nomeSalvo: string) => {
        setArquivosParaRemover(prev => [...prev, nomeSalvo]);
    };

    const handleAddFiles = () => {
        fileInputRef.current?.click();
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(e.target.files || []) as UploadedFile[];
        setNovosArquivos(prev => [...prev, ...files]);
        
        // Limpa o input para permitir selecionar o mesmo arquivo novamente
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const handleRemoveNovoArquivo = (index: number) => {
        setNovosArquivos(prev => prev.filter((_, i) => i !== index));
    };

    const formatFileSize = (bytes: number): string => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    return (
        <Modal show={show} onHide={onHide} size="lg" centered>
            <Modal.Header closeButton>
                <Modal.Title>Editar Anotação</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <form onSubmit={handleSubmit}>
                    <div className="form-group mb-3">
                        <label htmlFor="titulo" className="form-label">Título</label>
                        <input
                            type="text"
                            className="form-control"
                            id="titulo"
                            value={titulo}
                            onChange={(e) => setTitulo(e.target.value)}
                            required
                        />
                    </div>
                    <div className="form-group mb-3">
                        <label htmlFor="observacoes" className="form-label">Observações</label>
                        <textarea
                            className="form-control"
                            id="observacoes"
                            rows={4}
                            value={observacoes}
                            onChange={(e) => setObservacoes(e.target.value)}
                            required
                        />
                    </div>

                    <div className="mb-3">
                        <div className="d-flex justify-content-between align-items-center mb-2">
                            <label className="form-label mb-0">Arquivos</label>
                            <button
                                type="button"
                                className="btn btn-primary btn-sm"
                                onClick={handleAddFiles}
                            >
                                <i className="ti ti-plus"></i> Adicionar Arquivo
                            </button>
                            <input
                                type="file"
                                ref={fileInputRef}
                                onChange={handleFileChange}
                                multiple
                                style={{ display: 'none' }}
                            />
                        </div>

                        {/* Arquivos existentes */}
                        {note?.arquivos && note.arquivos.map((arquivo, index) => (
                            !arquivosParaRemover.includes(arquivo.nome_salvo) && (
                                <div key={index} className="d-flex align-items-center justify-content-between border p-2 rounded mb-2">
                                    <div>
                                        <span className="me-2">{arquivo.nome_original}</span>
                                        <small className="text-muted">({formatFileSize(arquivo.tamanho)})</small>
                                    </div>
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-danger"
                                        onClick={() => handleRemoveFile(arquivo.nome_salvo)}
                                    >
                                        <i className="ti ti-trash"></i>
                                    </button>
                                </div>
                            )
                        ))}

                        {/* Novos arquivos */}
                        {novosArquivos.map((arquivo, index) => (
                            <div key={index} className="d-flex align-items-center justify-content-between border p-2 rounded mb-2">
                                <div>
                                    <span className="me-2">{arquivo.name}</span>
                                    <small className="text-muted">({formatFileSize(arquivo.size)})</small>
                                </div>
                                <button
                                    type="button"
                                    className="btn btn-sm btn-danger"
                                    onClick={() => handleRemoveNovoArquivo(index)}
                                >
                                    <i className="ti ti-trash"></i>
                                </button>
                            </div>
                        ))}
                    </div>

                    <div className="d-flex justify-content-end gap-2">
                        <button
                            type="button"
                            className="btn btn-light"
                            onClick={onHide}
                            disabled={isSubmitting}
                        >
                            Cancelar
                        </button>
                        <button
                            type="submit"
                            className="btn btn-primary"
                            disabled={isSubmitting}
                        >
                            {isSubmitting ? (
                                <>
                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                    Salvando...
                                </>
                            ) : (
                                'Salvar Alterações'
                            )}
                        </button>
                    </div>
                </form>
            </Modal.Body>
        </Modal>
    );
};

export default EditNoteModal;